import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCalendarCheck } from "react-icons/fa";
import logo from "../assets/iqralogo.png";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      
      const sections = ["home", "about", "services", "doctor", "gallery", "contact"];
      let current = "home";
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            current = section;
          }
        }
      }
      setActiveSection(current);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  const navItems = [
    { name: "Home", id: "home" },
    { name: "About", id: "about" },
    { name: "Services", id: "services" },
    { name: "Doctors", id: "doctor" },
    { name: "Gallery", id: "gallery" },
    { name: "Contact", id: "contact" },
  ];

  const handleSmoothScroll = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMenuOpen(false);
      setActiveSection(sectionId);
    }
  };

  return (
    <>
      <nav className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        scrolled 
          ? "bg-white/90 backdrop-blur-2xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] border-b border-white/20" 
          : "bg-white/60 backdrop-blur-lg border-b border-white/20"
      }`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4 sm:px-6 lg:px-8 py-3 md:py-4">
          
          <motion.a 
            href="#home" 
            onClick={(e) => handleSmoothScroll(e, 'home')}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center group"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-teal-600 rounded-full blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>
              <img 
                src={logo} 
                alt="Dr. Sainulabid's IQRA Multispeciality Dental Clinic" 
                className="relative h-14 sm:h-16 md:h-[72px] object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </div>
          </motion.a>
          
          <div className="hidden md:flex items-center gap-10 lg:gap-12">
            {navItems.map((item, index) => (
              <motion.a
                key={item.name}
                href={`#${item.id}`}
                onClick={(e) => handleSmoothScroll(e, item.id)}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="relative group"
              >
                <span className={`transition-colors duration-300 font-medium text-sm lg:text-base ${
                  activeSection === item.id 
                    ? "text-teal-600" 
                    : "text-[#1E293B] hover:text-teal-600"
                }`}>
                  {item.name}
                </span>
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] transition-all duration-300 rounded-full ${
                  activeSection === item.id ? "w-full" : "w-0 group-hover:w-full"
                }`}></span>
              </motion.a>
            ))}
          </div>
          
          <motion.a
            href="#contact"
            onClick={(e) => handleSmoothScroll(e, 'contact')}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="hidden md:block relative overflow-hidden group ml-0"
          >
            <div className="relative z-10 px-5 py-2 rounded-full bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] text-white font-semibold text-sm transition-all duration-300 group-hover:scale-105 shadow-md group-hover:shadow-xl">
              Book Appointment
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#1E8A94] to-[#3ABFA8] rounded-full translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
          </motion.a>
          
          <motion.button 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="md:hidden relative w-10 h-10 flex items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 transition-all duration-300"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <div className="w-5 h-4 flex flex-col justify-between">
              <motion.span 
                animate={{ rotate: menuOpen ? 45 : 0, y: menuOpen ? 7 : 0 }}
                className="w-full h-0.5 bg-[#1E293B] rounded-full transition-all duration-300"
              />
              <motion.span 
                animate={{ opacity: menuOpen ? 0 : 1 }}
                className="w-full h-0.5 bg-[#1E293B] rounded-full transition-all duration-300"
              />
              <motion.span 
                animate={{ rotate: menuOpen ? -45 : 0, y: menuOpen ? -7 : 0 }}
                className="w-full h-0.5 bg-[#1E293B] rounded-full transition-all duration-300"
              />
            </div>
          </motion.button>
        </div>
      </nav>

      {/* ✅ Premium Compact Mobile Menu - Final */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
              onClick={() => setMenuOpen(false)}
            />
            
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-screen w-[85%] max-w-sm bg-white/95 backdrop-blur-2xl z-50 md:hidden shadow-2xl rounded-l-2xl overflow-y-auto"
            >
              <button 
                onClick={() => setMenuOpen(false)}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition"
              >
                <svg className="w-5 h-5 text-[#1E293B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="pt-16 pb-3 px-6">
                <img src={logo} alt="Dr. Sainulabid's IQRA Multispeciality Dental Clinic" className="h-12 object-contain" />
                <p className="text-xs text-[#64748B] mt-1.5 tracking-wide">
                  • Say It With A Smile
                </p>
              </div>
              
              <div className="px-4 pb-2">
                {navItems.map((item, index) => (
                  <motion.a
                    key={item.name}
                    href={`#${item.id}`}
                    onClick={(e) => handleSmoothScroll(e, item.id)}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center justify-between px-4 py-3.5 rounded-xl transition-all duration-200 ${
                      activeSection === item.id 
                        ? "bg-teal-50 text-teal-600" 
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <span className={`font-medium text-sm ${
                      activeSection === item.id 
                        ? "text-teal-600" 
                        : "text-[#1E293B]"
                    }`}>
                      {item.name}
                    </span>
                    <span className={`text-sm transition ${
                      activeSection === item.id 
                        ? "opacity-100 text-teal-600" 
                        : "opacity-0 group-hover:opacity-100 text-gray-400"
                    }`}>→</span>
                  </motion.a>
                ))}
              </div>
              
              <div className="px-4 pt-2 pb-5">
                <a
                  href="#contact"
                  onClick={(e) => handleSmoothScroll(e, 'contact')}
                  className="flex items-center justify-center gap-2 w-full text-center bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] text-white py-3.5 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-sm"
                >
                  <FaCalendarCheck className="text-sm" />
                  Book Appointment
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}